import { useParams, Link, useNavigate } from "react-router-dom";
import { useActiveLeague } from "../hooks/useActiveLeague";
import { useAuth } from "../context/AuthContext";
import React from "react";
import { toast } from 'react-toastify';

import { LeaguePageNav } from "../components/league/LeaguePageNav";
import { TeamsBlock } from "../components/league/TeamsBlock";
import { LeagueAvatar } from "../components/league/LeagueAvatar";

import { useGetLeagueBySlug, useGenerateFixtures, useCreateLeagueInviteLink } from "../api/leaguesApi.js";
import { useStartMultiplayerSeason } from "../api/multiplayerApi.js";
import { isAutoSquadSeasonLaunch, isPreSeason } from "../utils/seasonStatus";
import { Popup } from "../components/common/Popup";
import { canBeginSeason } from "../utils/seasonStatus";
import { Spinner } from "../components/common/Spinner";
import { hasCustomLeagueImage } from "../utils/leagueAvatar";
import { getSeasonStartedMessage } from "../utils/seasonKickoff";
import { getOwnerJoinabilityErrorMessage } from "../utils/inviteErrors";
import { QuickplayMatchHistoryPage } from "./QuickplayMatchHistoryPage";

function LeagueTablePage() {
  const { slug } = useParams(); // Retain for data fetching if it's the direct key
  const { activeLeague } = useActiveLeague();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: slugLeagueData, isLoading: isLoadingLeague, refetch: refetchLeagueData } = useGetLeagueBySlug(slug);

  // NEW: normalise API response so code works with either { league: {...} } or { ... }
  const rawLeague = slugLeagueData?.data;
  const league = rawLeague?.league ?? rawLeague;
  const leagueTeamCount = Array.isArray(league?.teams) ? league.teams.length : 0;
  const showLeagueTitle = Boolean(league?.name);
  const showLeagueAvatar = hasCustomLeagueImage(league?.image);
  const generateFixturesMutation = useGenerateFixtures();
  const startMultiplayerSeason = useStartMultiplayerSeason();
  const createInviteLink = useCreateLeagueInviteLink();
  const leagueTitle = !showLeagueTitle ? null : showLeagueAvatar ? (
    <h1 className="league-title-with-avatar">
      <LeagueAvatar
        image={league.image}
        name={league.name}
        className="league-title-avatar"
        decorative
      />
      <span>{league.name}</span>
    </h1>
  ) : (
    <h1>{league.name}</h1>
  );

  // Handle both populated owner object (owner._id) and plain owner ID string
  const leagueOwnerId = league?.owner?._id || league?.owner;
  const isLeagueOwner = leagueOwnerId && user?._id && leagueOwnerId.toString() === user._id.toString();
  const generateFixtures = (leagueId) => {
    generateFixturesMutation.mutate(leagueId, {
      onSuccess: (data) => {
        console.log("Fixtures generated successfully:", data);
        toast.success(data.message || "Fixtures generated successfully!");
        navigate(activeLeague ? `/${activeLeague.slug}/fixtures` : '/switch-league');
      },
      onError: (error) => {
        console.error("Error generating fixtures:", error);
        const errorMessage = error.response?.data?.error || "Failed to generate fixtures. Please try again.";
        toast.error(errorMessage);
      },
    });
  };

  const [showConfirmPopup, setShowConfirmPopup] = React.useState(false);

  const handleBeginSeason = async () => {
    if (!league?._id) {
      console.error("League ID not available");
      toast.error("Could not identify the league. Please refresh.");
      return;
    }

    // The multiplayer start endpoint schedules the round-1 kickoff; odd team
    // counts are handled by bye rounds in the fixture scheduler.
    if (isAutoSquadSeasonLaunch(league)) {
      try {
        const result = await startMultiplayerSeason.mutateAsync({
          leagueId: league._id,
          teamCount: leagueTeamCount
        });
        toast.success(getSeasonStartedMessage(result?.roundOneKickoff));
        navigate(activeLeague ? `/${activeLeague.slug}/fixtures` : '/switch-league');
      } catch (error) {
        console.error("Error starting multiplayer season:", error);
        toast.error(error.response?.data?.error || "Failed to start the season. Please try again.");
      }
      return;
    }

    if (league?.matches && league.matches.length > 0) {
      setShowConfirmPopup(true);
      return;
    }

    generateFixtures(league._id);
  };

  const confirmGenerateNextSeason = () => {
    setShowConfirmPopup(false);
    if (league?._id) {
      generateFixtures(league._id);
    }
  };

  // Owners of a not-yet-started auto-squad league can re-copy the multi-use
  // invite link here; the create page only holds it in transient state.
  const handleCopyInviteLink = async () => {
    if (!league?._id) {
      toast.error("Could not identify the league. Please refresh.");
      return;
    }
    try {
      const { url } = await createInviteLink.mutateAsync(league._id);
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied! Share it with your friends.");
    } catch (error) {
      console.error("Error copying invite link:", error);
      toast.error(getOwnerJoinabilityErrorMessage(error) || error.response?.data?.error || "Could not copy the invite link. Please try again.");
    }
  };

  if (league?.gameMode === 'quickplay') {
    return <QuickplayMatchHistoryPage league={league} />;
  }

  return (
    <div className="page-league">
      <div className="wrapper">
        <LeaguePageNav />

        <div className="title-block">
          {isLeagueOwner && canBeginSeason(league) && (
            <div className="ctrl-block owner-season-controls">
              {isPreSeason(league) && (
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleCopyInviteLink}
                  disabled={createInviteLink.isPending}
                >
                  {createInviteLink.isPending ? 'Copying...' : 'Copy invite link'}
                </button>
              )}
              <button
                type="button"
                className="submit-btn"
                onClick={handleBeginSeason}
                disabled={generateFixturesMutation.isLoading || startMultiplayerSeason.isPending || leagueTeamCount < 2}
              >
                {(generateFixturesMutation.isLoading || startMultiplayerSeason.isPending) ? 'Starting...' : 'Begin season'}
              </button>
              {leagueTeamCount < 2 && (
                <div className="empty-message">
                  At least one more team needs to join the league before the season can start.
                </div>
              )}
            </div>
          )}

          {leagueTitle}

          <div className="ctrl-block flex-block">
            <Link to={"/switch-league"} className="read-more-btn">
              Switch league
              <span className="ico icon-arrow-right" />
            </Link>
            <Link to={activeLeague ? `/${activeLeague.slug}/league-settings` : '#'}>
              <span className="ico-btn icon-gear" />
            </Link>
          </div>
        </div>

        {league?.seasonState === 'completed' && (
          <Link to={`/${slug}/season-awards`} className="season-awards-banner">
            <strong>Season {league?.season || 1} complete!</strong>
            <span>See the awards and play your Season Wrapped</span>
            <span className="ico icon-arrow-right" />
          </Link>
        )}

        {isLoadingLeague ? (
          <div className="loader">
            <Spinner />
          </div>
        ) : (
          <TeamsBlock teams={league?.teams} />
        )}

        {/* Confirmation Popup */}
        <Popup popOpen={showConfirmPopup} close={() => setShowConfirmPopup(false)}>
          <h3>Season {league?.season || 1} fixtures already created</h3>
          <p>Proceed with creating Season {(league?.season || 1) + 1} fixtures?</p>
          <div className="btn-block flex-block">
            <button type="button" className="cancel-btn" onClick={() => setShowConfirmPopup(false)}>Cancel</button>
            <button type="button" className="confirm-btn" onClick={confirmGenerateNextSeason}>Confirm</button>
          </div>
        </Popup>
      </div>
    </div>
  );
}

export { LeagueTablePage };
